import { useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import ToggleField from '@/components/Indicators/ToggleField'
import { browserTimeZone, compactSymbol, sameSymbol } from '@/components/PriceAlerts/priceAlertFormat'

// Toggle «Nueva señal» del panel de un indicador, cableado al motor de
// alertas (WP-3 fase B, regla «ningún interruptor puede mentir»).
//
// - Fuente de verdad: la tabla `alerts` (la lista de usePriceAlerts), NUNCA
//   localStorage. El toggle está encendido si existe una alerta viva (activa
//   o pausada) de este indicador para ese símbolo×TF con alguno de
//   `eventCodes`; al recargar la página se reconstruye desde el backend.
// - Encender = crear las alertas que falten (feed BingX, símbolo y TF del
//   chart, config ACTUAL del panel, «una vez por cierre de vela», sin
//   vencimiento). Apagar = borrar las que existan. Sin estado optimista:
//   cada mutación espera al backend y recarga la lista (usePriceAlerts).
// - Canales: popup siempre; Telegram si el usuario tiene Telegram vinculado
//   (`telegram_linked` del backend = users.telegram_chat_id) y el panel no
//   tiene ya su propia suscripción Telegram (`telegram={false}`, NASDAQ:
//   evita el doble aviso por Telegram con la suscripción de la matriz).
// - Sin `alerts` (app privada, captura) no se pinta: no hay motor detrás.
export function signalAlertMatches(alerts, { indicatorId, eventCodes, symbol, timeframe }) {
  return (alerts || []).filter(a => (
    a.series_kind === 'indicator_event'
    && a.indicator_id === indicatorId
    && a.feed === 'bingx'
    && a.timeframe === timeframe
    && eventCodes.includes(a.event_code)
    && (a.status === 'active' || a.status === 'paused')
    && sameSymbol(a.symbol, symbol)
  ))
}

export default function SignalAlertToggle({
  alerts: api,
  indicatorId,
  indicatorName,
  version,
  eventCodes,
  label = 'Nueva señal',
  symbol,
  timeframe,
  fixedTimeframe = null,
  timeframes = ['5m', '15m', '1h', '4h', '1d'],
  config,
  telegram = true,
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  if (!api || !api.featureEnabled) return null

  const tf = fixedTimeframe || timeframe
  const matches = signalAlertMatches(api.alerts, { indicatorId, eventCodes, symbol, timeframe: tf })
  const on = matches.length > 0
  const paused = on && matches.every(a => a.status === 'paused')
  const withTelegram = telegram && api.telegramLinked

  let reason = null
  if (!api.loaded) reason = 'Cargando tus alertas…'
  else if (!api.canAlert) reason = 'Las alertas se evalúan con velas BingX: cambia la fuente del chart a BingX.'
  else if (!api.engineEnabled) reason = 'Llega al activar el motor de cierre de vela.'
  else if (!symbol) reason = 'Elige un activo en el chart.'
  else if (!timeframes.includes(tf)) reason = `Se alerta en ${timeframes.join(', ')}.`

  const toggle = async () => {
    if (busy || reason) return
    setBusy(true)
    setError(null)
    if (on) {
      for (const a of matches) {
        const res = await api.remove(a.id)
        if (!res.ok) { setError(res.error); break }
      }
    } else {
      for (const code of eventCodes) {
        const res = await api.create({
          feed: 'bingx',
          symbol,
          timeframe: tf,
          series_kind: 'indicator_event',
          indicator_id: indicatorId,
          indicator_version: version,
          event_code: code,
          operator: 'occurs',
          value_1: null,
          value_2: null,
          params: config ?? null,
          activation: 'once_per_bar_close',
          name: `${indicatorName}: nueva señal`.slice(0, 80),
          message: null,
          channels: ['popup', ...(withTelegram ? ['telegram'] : [])],
          expiry_kind: 'indefinite',
          expires_at: null,
          display_tz: browserTimeZone(),
        })
        if (!res.ok) { setError(res.error); break }
      }
    }
    setBusy(false)
  }

  const where = `${compactSymbol(symbol || '')} · ${tf}`
  return (
    <div className="space-y-1" data-testid={`signal-alert-${indicatorId}`}>
      <ToggleField
        label={`${label} (${where})`}
        value={on}
        onChange={toggle}
        disabled={busy || Boolean(reason)}
        data-testid="signal-alert-toggle"
      />
      <p className="text-[10px] leading-snug" style={{ color: 'var(--ink-soft, #4A5A52)' }} data-testid="signal-alert-note">
        {reason || (on
          ? `Alerta del motor activa${paused ? ' (pausada en tu lista de alertas)' : ''}: avisa al cierre de cada vela con señal${withTelegram || matches.some(a => a.channels?.includes('telegram')) ? ' (aviso en el chart y Telegram)' : ' (aviso en el chart)'}. Usa la config del panel copiada al activarla; si la cambias, apágalo y vuelve a encenderlo.`
          : `Crea una alerta del motor (servidor, sigue aunque cierres la pestaña): avisa al cierre de cada vela con señal en ${where}${withTelegram ? ', en el chart y por Telegram' : ', en el chart'}. La verás en tu lista de alertas.`)}
      </p>
      {error && (
        <div className="flex items-start gap-1.5 rounded border px-2 py-1.5 text-[10px]" role="alert"
          style={{ background: 'rgba(183,28,28,0.08)', borderColor: 'rgba(183,28,28,0.35)', color: '#B71C1C' }}>
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          <span className="flex-1">{error}</span>
        </div>
      )}
    </div>
  )
}
